import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Grid, Collapse, IconButton,
  Alert, Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { Trade, Client } from '../types';
import { dailyBrokerageContribution } from '../utils/brokerage';
import { istDay } from '../utils/formatters';
import { tradeService } from '../services/trade.service';
import { clientService } from '../services/client.service';
import { marketService } from '../services/market.service';
import PageHeader from '../components/common/PageHeader';

// ============================================================================
// CONSTANTS
// ============================================================================

const COLORS = {
  primary: '#4F46E5',
  primaryDark: '#3730A3',
  positive: '#059669',
  negative: '#DC2626',
  warning: '#D97706',
  blue: '#2563EB',
  textPrimary: '#0F172A',
  textSecondary: '#64748B',
  textMuted: '#94A3B8',
  border: '#E2E8F0',
  borderStrong: '#CBD5E1',
  pageBg: '#F8FAFC',
  surface: '#FFFFFF',
};

const formatCurrency = (val: number): string =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);

// ============================================================================
// DATE HELPERS
// ============================================================================

type DateMode = 'day' | 'week' | 'month' | 'all';

/** Strip time from a Date, returning midnight local */
function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function isToday(d: Date): boolean {
  return isSameDay(d, new Date());
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function formatDateNav(d: Date): string {
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

/** Monday of the week containing d */
function weekStart(d: Date): Date {
  const day = startOfDay(d);
  const dow = day.getDay();
  return addDays(day, -(dow === 0 ? 6 : dow - 1));
}

/** Sunday of the week containing d */
function weekEnd(d: Date): Date {
  return addDays(weekStart(d), 6);
}

/** 26th-25th billing cycle containing d */
function monthCycleRange(d: Date): { start: Date; end: Date } {
  const day = d.getDate();
  const cs = day >= 26
    ? new Date(d.getFullYear(), d.getMonth(), 26)
    : new Date(d.getFullYear(), d.getMonth() - 1, 26);
  const ce = day >= 26
    ? new Date(d.getFullYear(), d.getMonth() + 1, 25)
    : new Date(d.getFullYear(), d.getMonth(), 25);
  return { start: cs, end: ce };
}

function getDateRange(mode: DateMode, sel: Date): { start: Date; end: Date } | null {
  if (mode === 'all') return null;
  if (mode === 'day') {
    const s = startOfDay(sel);
    return { start: s, end: new Date(s.getTime() + 86400000 - 1) };
  }
  if (mode === 'week') {
    return { start: weekStart(sel), end: weekEnd(sel) };
  }
  // month (26th-25th cycle)
  return monthCycleRange(sel);
}

// (isTradeInRange removed — daily attribution now compares IST day strings via istDay)

function dateRangeLabel(mode: DateMode, sel: Date): string {
  if (mode === 'all') return 'All Time';
  if (mode === 'day') return formatDateNav(sel);
  if (mode === 'week') {
    const ws = weekStart(sel);
    const we = weekEnd(sel);
    const fmt = (d: Date) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    return `${fmt(ws)} - ${fmt(we)}`;
  }
  const { start, end } = monthCycleRange(sel);
  const fmt = (d: Date) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  return `${fmt(start)} - ${fmt(end)}`;
}

// ============================================================================
// TYPES
// ============================================================================

interface DayBreakdown {
  date: string;
  trades: number;
  equityBrk: number;
  intraBrk: number;
  futureBrk: number;
  optionBrk: number;
  totalBrk: number;
  dayPnL: number;
}

interface ClientRow {
  clientId: string;
  clientName: string;
  trades: number;
  turnover: number;
  equityBrk: number;
  intraBrk: number;
  futureBrk: number;
  optionBrk: number;
  totalBrk: number;
  realisedPnL: number;
  dayBreakdowns: DayBreakdown[];
}

// ============================================================================
// SHARED TABLE CELL STYLES
// ============================================================================

const headerCellSx = {
  fontWeight: 600,
  fontSize: '0.68rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
  color: '#64748B',
  py: 1.5,
  borderBottom: '1px solid #E2E8F0',
  whiteSpace: 'nowrap' as const,
};

const currencyCellSx = {
  fontWeight: 500,
  fontFeatureSettings: '"tnum"',
  fontVariantNumeric: 'tabular-nums' as const,
  fontSize: '0.85rem',
  color: '#0F172A',
};

// ============================================================================
// COMPONENT
// ============================================================================

export function Analytics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [dateMode, setDateMode] = useState<DateMode>('day');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [expandedClient, setExpandedClient] = useState<string | null>(null);

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [allTrades, allClients] = await Promise.all([
          tradeService.getTrades(),
          clientService.getClients(),
        ]);
        setTrades(allTrades);
        setClients(allClients);
      } catch (err: any) {
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Live price refresh for open positions
  const refreshPrices = useCallback(async () => {
    const scrips = [...new Set(trades.filter((t) => t.status === 'OPEN' && !t.isDeleted).map((t) => t.scrip).filter(Boolean))];
    if (scrips.length === 0) return;
    try {
      const { prices } = await marketService.getQuotes(scrips);
      setTrades((prev) =>
        prev.map((t) => {
          if (t.status === 'OPEN' && prices[t.scrip]) {
            const livePrice = prices[t.scrip].price;
            if (!livePrice || livePrice <= 0) return t;
            const balanceLots = t.purchaseLots - (t.saleLots || 0);
            const investedAmount = balanceLots * t.lotSize * t.purchasePrice;
            const currentValue = livePrice * balanceLots * t.lotSize;
            const isSellFirst = t.direction === 'SELL';
            const unrealizedPnL = isSellFirst ? (investedAmount - currentValue) : (currentValue - investedAmount);
            return { ...t, currentPrice: livePrice, currentValue, unrealizedPnL, totalPnL: (t.realizedPnL || 0) + unrealizedPnL };
          }
          return t;
        })
      );
    } catch { /* non-fatal */ }
  }, [trades]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const istHour = (now.getUTCHours() + 5) % 24 + (now.getUTCMinutes() + 30 >= 60 ? 1 : 0);
      const istMin = (now.getUTCMinutes() + 30) % 60;
      const day = now.getUTCDay();
      if (day >= 1 && day <= 5 && (istHour > 9 || (istHour === 9 && istMin >= 0)) && (istHour < 15 || (istHour === 15 && istMin <= 35))) {
        refreshPrices();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [refreshPrices]);

  // Client map
  const clientMap = useMemo(() => {
    const map: Record<string, Client> = {};
    clients.forEach((c) => { map[c.clientId] = c; });
    return map;
  }, [clients]);

  // Compute client rows — Bajaj-style daily attribution.
  // Brokerage is split: buy-side commission books on the BUY day, sell-side
  // commission books on the SELL day. A single trade therefore contributes
  // to two day buckets (or one for intraday). Realised P&L stays attributed
  // to the close (sell) day. See utils/brokerage.ts for the contribution
  // helper that encodes the rule.
  const clientRows = useMemo((): ClientRow[] => {
    const activeTrades = trades.filter((t) => !t.isDeleted);
    const range = getDateRange(dateMode, selectedDate);
    const startStr = range ? istDay(range.start.toISOString()) : '';
    const endStr = range ? istDay(range.end.toISOString()) : '';

    // Group by clientId first — we evaluate every trade per client and
    // attribute contributions to relevant days.
    const grouped: Record<string, Trade[]> = {};
    activeTrades.forEach((t) => {
      if (!grouped[t.clientId]) grouped[t.clientId] = [];
      grouped[t.clientId].push(t);
    });

    const rows: ClientRow[] = Object.entries(grouped).map(([clientId, clientTrades]) => {
      const client = clientMap[clientId];
      const rates = client?.brokerageRates || ({} as any);

      type DayBucket = {
        date: string;
        tradeIds: Set<string>;
        equityBrk: number;
        intraBrk: number;
        futureBrk: number;
        optionBrk: number;
        dayPnL: number;
        turnover: number;
      };
      const buckets: Record<string, DayBucket> = {};
      const ensureBucket = (d: string): DayBucket => {
        if (!buckets[d]) {
          buckets[d] = {
            date: d, tradeIds: new Set(),
            equityBrk: 0, intraBrk: 0, futureBrk: 0, optionBrk: 0,
            dayPnL: 0, turnover: 0,
          };
        }
        return buckets[d];
      };
      const categoryToBucketKey = (cat: 'delivery' | 'intra' | 'future' | 'option') =>
        cat === 'delivery' ? 'equityBrk' :
        cat === 'intra' ? 'intraBrk' :
        cat === 'future' ? 'futureBrk' : 'optionBrk';

      clientTrades.forEach((t) => {
        const c = dailyBrokerageContribution(t, rates);
        const buyVal = t.purchaseLots * t.lotSize * t.purchasePrice;
        const sellVal = (t.saleLots || 0) * t.lotSize * (t.salePrice || 0);

        // Buy-side attribution
        if (c.buy > 0 && c.buyDay) {
          const b = ensureBucket(c.buyDay);
          (b as any)[categoryToBucketKey(c.category)] += c.buy;
          b.tradeIds.add(t.tradeId);
          b.turnover += buyVal;
        }
        // Sell-side attribution + realised P&L on close day
        if (c.sell > 0 && c.sellDay) {
          const b = ensureBucket(c.sellDay);
          (b as any)[categoryToBucketKey(c.category)] += c.sell;
          b.tradeIds.add(t.tradeId);
          b.turnover += sellVal;
          // INTRA: helper puts full commission on `sell`. Turnover above
          // double-counts buyVal for intra (we already added it on buy-side).
          // For intraday c.buy === 0 but buyDay === sellDay, so no buyVal added
          // (the if above checks c.buy > 0). Net: intra turnover = buyVal+sellVal added on sellDay only. ✓
        }
        if (c.sellDay) {
          ensureBucket(c.sellDay).dayPnL += t.realizedPnL || 0;
        }
      });

      // Apply date range filter to day buckets
      const allDays = Object.values(buckets);
      const visibleDays = range
        ? allDays.filter((b) => b.date >= startStr && b.date <= endStr)
        : allDays;

      // Build day breakdowns + totals
      let totalEquityBrk = 0, totalIntraBrk = 0, totalFutureBrk = 0, totalOptionBrk = 0;
      let totalTurnover = 0, totalRealisedPnL = 0;
      const seenTrades = new Set<string>();

      const dayBreakdowns: DayBreakdown[] = visibleDays
        .sort((a, b) => b.date.localeCompare(a.date))
        .map((b) => {
          totalEquityBrk += b.equityBrk;
          totalIntraBrk += b.intraBrk;
          totalFutureBrk += b.futureBrk;
          totalOptionBrk += b.optionBrk;
          totalTurnover += b.turnover;
          totalRealisedPnL += b.dayPnL;
          b.tradeIds.forEach((id) => seenTrades.add(id));
          return {
            date: b.date,
            trades: b.tradeIds.size,
            equityBrk: b.equityBrk,
            intraBrk: b.intraBrk,
            futureBrk: b.futureBrk,
            optionBrk: b.optionBrk,
            totalBrk: b.equityBrk + b.intraBrk + b.futureBrk + b.optionBrk,
            dayPnL: b.dayPnL,
          };
        });

      return {
        clientId,
        clientName: client?.name || clientTrades[0]?.clientName || clientId,
        trades: seenTrades.size,
        turnover: totalTurnover,
        equityBrk: totalEquityBrk,
        intraBrk: totalIntraBrk,
        futureBrk: totalFutureBrk,
        optionBrk: totalOptionBrk,
        totalBrk: totalEquityBrk + totalIntraBrk + totalFutureBrk + totalOptionBrk,
        realisedPnL: totalRealisedPnL,
        dayBreakdowns,
      };
    });

    // Filter out clients with zero activity in range
    const filtered = rows.filter((r) => r.dayBreakdowns.length > 0);
    filtered.sort((a, b) => b.totalBrk - a.totalBrk);
    return filtered;
  }, [trades, clients, clientMap, dateMode, selectedDate]);

  // Summary totals
  const summary = useMemo(() => {
    const activeClients = clients.filter((c) => c.isActive).length;
    let totalBrk = 0;
    let totalTurnover = 0;
    let totalPnL = 0;
    clientRows.forEach((r) => {
      totalBrk += r.totalBrk;
      totalTurnover += r.turnover;
      totalPnL += r.realisedPnL;
    });
    return { activeClients, totalBrk, totalTurnover, totalPnL };
  }, [clients, clientRows]);

  const toggleExpand = (clientId: string) => {
    setExpandedClient((prev) => (prev === clientId ? null : clientId));
  };

  // Date navigation handlers
  const goPrevDay = () => setSelectedDate((d) => addDays(d, -1));
  const goNextDay = () => {
    if (!isToday(selectedDate)) setSelectedDate((d) => addDays(d, 1));
  };
  const goToday = () => {
    setSelectedDate(new Date());
    setDateMode('day');
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const pnlPositive = summary.totalPnL >= 0;
  const summaryCards: {
    label: string;
    value: string;
    icon: React.ReactNode;
    accent: string;
    accentBg: string;
    valueColor?: string;
  }[] = [
    {
      label: 'Active Clients',
      value: loading ? '—' : summary.activeClients.toString(),
      icon: <PeopleOutlineIcon sx={{ fontSize: 20 }} />,
      accent: '#4F46E5',
      accentBg: '#EEF2FF',
    },
    {
      label: 'Total Brokerage',
      value: loading ? '—' : formatCurrency(summary.totalBrk),
      icon: <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 20 }} />,
      accent: '#D97706',
      accentBg: '#FEF3C7',
    },
    {
      label: 'Total Turnover',
      value: loading ? '—' : formatCurrency(summary.totalTurnover),
      icon: <ShowChartIcon sx={{ fontSize: 20 }} />,
      accent: '#2563EB',
      accentBg: '#DBEAFE',
    },
    {
      label: 'Realised P&L',
      value: loading ? '—' : formatCurrency(summary.totalPnL),
      icon: pnlPositive ? <TrendingUpIcon sx={{ fontSize: 20 }} /> : <TrendingDownIcon sx={{ fontSize: 20 }} />,
      accent: pnlPositive ? '#059669' : '#DC2626',
      accentBg: pnlPositive ? '#D1FAE5' : '#FEE2E2',
      valueColor: pnlPositive ? '#059669' : '#DC2626',
    },
  ];

  const segmentedModes: { key: DateMode; label: string }[] = [
    { key: 'day', label: 'Day' },
    { key: 'week', label: 'Week' },
    { key: 'month', label: 'Month' },
    { key: 'all', label: 'All' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: COLORS.pageBg, p: { xs: 2, md: 4 } }}>
      <PageHeader
        eyebrow="Reports"
        title="Client Analytics"
        subtitle="Client-wise brokerage breakdown with day-wise drill-down"
      />

      {/* Summary Cards */}
      <Grid container spacing={2.5} mb={3.5}>
        {summaryCards.map((card) => (
          <Grid item xs={6} md={3} key={card.label}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                bgcolor: COLORS.surface,
                border: `1px solid ${COLORS.border}`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: COLORS.borderStrong,
                  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.04)',
                },
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: COLORS.textSecondary,
                    letterSpacing: '0.04em',
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                  }}
                >
                  {card.label}
                </Typography>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: card.accentBg,
                    color: card.accent,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {card.icon}
                </Box>
              </Box>
              <Typography
                sx={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: card.valueColor || COLORS.textPrimary,
                  letterSpacing: '-0.02em',
                  fontFeatureSettings: '"tnum"',
                  lineHeight: 1.15,
                }}
              >
                {card.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Date Navigation Toolbar */}
      <Paper
        elevation={0}
        sx={{
          mb: 2.5,
          p: 1.25,
          borderRadius: 2,
          bgcolor: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        {/* Segmented mode switcher */}
        <Box
          sx={{
            display: 'inline-flex',
            p: 0.375,
            bgcolor: '#F1F5F9',
            borderRadius: 1.5,
            gap: 0.25,
          }}
        >
          {segmentedModes.map((m) => {
            const active = dateMode === m.key;
            return (
              <Box
                key={m.key}
                onClick={() => {
                  setDateMode(m.key);
                  if (m.key !== 'day') setSelectedDate(new Date());
                }}
                sx={{
                  px: 1.75,
                  py: 0.625,
                  borderRadius: 1,
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  color: active ? COLORS.textPrimary : COLORS.textSecondary,
                  bgcolor: active ? COLORS.surface : 'transparent',
                  boxShadow: active ? '0 1px 2px rgba(15, 23, 42, 0.08)' : 'none',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    color: COLORS.textPrimary,
                  },
                }}
              >
                {m.label}
              </Box>
            );
          })}
        </Box>

        <Box sx={{ width: '1px', height: 24, bgcolor: COLORS.border }} />

        {/* Day nav arrows + chip */}
        {dateMode === 'day' && (
          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton
              size="small"
              onClick={goPrevDay}
              sx={{
                width: 30,
                height: 30,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 1,
                color: COLORS.textSecondary,
                '&:hover': { bgcolor: '#F8FAFC', color: COLORS.textPrimary, borderColor: COLORS.borderStrong },
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                minWidth: 180,
                textAlign: 'center',
                bgcolor: '#F8FAFC',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 1,
                fontSize: '0.8rem',
                fontWeight: 600,
                color: COLORS.textPrimary,
                fontFeatureSettings: '"tnum"',
              }}
            >
              {formatDateNav(selectedDate)}
            </Box>
            <IconButton
              size="small"
              onClick={goNextDay}
              disabled={isToday(selectedDate)}
              sx={{
                width: 30,
                height: 30,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 1,
                color: COLORS.textSecondary,
                '&:hover': { bgcolor: '#F8FAFC', color: COLORS.textPrimary, borderColor: COLORS.borderStrong },
                '&.Mui-disabled': { opacity: 0.4 },
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
            {!isToday(selectedDate) && (
              <Button
                size="small"
                onClick={goToday}
                sx={{
                  ml: 0.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.78rem',
                  color: COLORS.primary,
                  px: 1.25,
                  minWidth: 'auto',
                  '&:hover': { bgcolor: '#EEF2FF' },
                }}
              >
                Today
              </Button>
            )}
          </Box>
        )}

        {(dateMode === 'week' || dateMode === 'month') && (
          <Box
            sx={{
              px: 1.5,
              py: 0.75,
              bgcolor: '#F8FAFC',
              border: `1px solid ${COLORS.border}`,
              borderRadius: 1,
              fontSize: '0.8rem',
              fontWeight: 600,
              color: COLORS.textPrimary,
              fontFeatureSettings: '"tnum"',
            }}
          >
            {dateRangeLabel(dateMode, selectedDate)}
          </Box>
        )}

        {dateMode === 'all' && (
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: COLORS.textSecondary }}>
            Showing all-time data
          </Typography>
        )}
      </Paper>

      {/* Loading indicator is handled inside the table empty state */}

      {/* Client Brokerage Table */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${COLORS.border}`, overflow: 'hidden', bgcolor: COLORS.surface }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                <TableCell sx={{ ...headerCellSx, width: 40 }} />
                <TableCell sx={headerCellSx}>Client</TableCell>
                <TableCell align="right" sx={headerCellSx}>Trades</TableCell>
                <TableCell align="right" sx={headerCellSx}>Turnover</TableCell>
                <TableCell align="right" sx={headerCellSx}>Delivery Brk</TableCell>
                <TableCell align="right" sx={headerCellSx}>Intra Brk</TableCell>
                <TableCell align="right" sx={headerCellSx}>Future Brk</TableCell>
                <TableCell align="right" sx={headerCellSx}>Option Brk</TableCell>
                <TableCell align="right" sx={headerCellSx}>Total Brk</TableCell>
                <TableCell align="right" sx={headerCellSx}>Realised P&L</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                    <Typography color={COLORS.textSecondary} fontSize="0.85rem">
                      Loading...
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && clientRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                    <Typography color={COLORS.textSecondary}>
                      No closed trades found for the selected period.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {clientRows.map((row) => {
                const isExpanded = expandedClient === row.clientId;
                return (
                  <React.Fragment key={row.clientId}>
                    {/* Client Summary Row */}
                    <TableRow
                      hover
                      sx={{
                        cursor: 'pointer',
                        bgcolor: isExpanded ? '#F8FAFC' : COLORS.surface,
                        '&:hover': { bgcolor: '#F8FAFC' },
                        '& td': { borderBottom: isExpanded ? 'none' : `1px solid ${COLORS.border}`, py: 1.75 },
                      }}
                      onClick={() => toggleExpand(row.clientId)}
                    >
                      <TableCell sx={{ width: 40, px: 1 }}>
                        <IconButton size="small">
                          {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography
                          fontWeight={600}
                          fontSize="0.88rem"
                          color={COLORS.textPrimary}
                          sx={{ cursor: 'pointer', '&:hover': { color: COLORS.primary } }}
                          onClick={(e) => { e.stopPropagation(); navigate(`/trades?clientId=${row.clientId}`); }}
                        >
                          {row.clientName}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-block',
                            minWidth: 30,
                            textAlign: 'center',
                            px: 0.8,
                            py: 0.2,
                            borderRadius: 999,
                            bgcolor: '#F1F5F9',
                            color: '#0F172A',
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            fontFeatureSettings: '"tnum"',
                          }}
                        >
                          {row.trades}
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ ...currencyCellSx, color: COLORS.blue, fontWeight: 600 }}>{formatCurrency(row.turnover)}</TableCell>
                      <TableCell align="right" sx={{ ...currencyCellSx, color: COLORS.warning, bgcolor: '#FFFBEB' }}>{formatCurrency(row.equityBrk)}</TableCell>
                      <TableCell align="right" sx={{ ...currencyCellSx, color: COLORS.warning, bgcolor: '#FFFBEB' }}>{formatCurrency(row.intraBrk)}</TableCell>
                      <TableCell align="right" sx={{ ...currencyCellSx, color: COLORS.warning, bgcolor: '#FFFBEB' }}>{formatCurrency(row.futureBrk)}</TableCell>
                      <TableCell align="right" sx={{ ...currencyCellSx, color: COLORS.warning, bgcolor: '#FFFBEB' }}>{formatCurrency(row.optionBrk)}</TableCell>
                      <TableCell align="right">
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-block',
                            px: 1,
                            py: 0.35,
                            borderRadius: 1,
                            bgcolor: '#EEF2FF',
                            color: '#4F46E5',
                            fontSize: '0.85rem',
                            fontWeight: 700,
                            fontFeatureSettings: '"tnum"',
                          }}
                        >
                          {formatCurrency(row.totalBrk)}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-block',
                            px: 1,
                            py: 0.35,
                            borderRadius: 1,
                            bgcolor: row.realisedPnL >= 0 ? '#D1FAE5' : '#FEE2E2',
                            color: row.realisedPnL >= 0 ? '#065F46' : '#991B1B',
                            fontSize: '0.85rem',
                            fontWeight: 700,
                            fontFeatureSettings: '"tnum"',
                          }}
                        >
                          {formatCurrency(row.realisedPnL)}
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Day-wise Breakdown (collapsible) */}
                    <TableRow>
                      <TableCell colSpan={10} sx={{ p: 0, border: 'none' }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ mx: 2, mb: 2, mt: 0.5 }}>
                            <Paper
                              variant="outlined"
                              sx={{ borderRadius: 1.5, overflow: 'hidden', bgcolor: '#FAFBFC', borderColor: COLORS.border }}
                            >
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ bgcolor: '#F1F5F9' }}>
                                    <TableCell sx={{ ...headerCellSx, fontSize: '0.65rem' }}>Date</TableCell>
                                    <TableCell align="right" sx={{ ...headerCellSx, fontSize: '0.65rem' }}>Trades</TableCell>
                                    <TableCell align="right" sx={{ ...headerCellSx, fontSize: '0.65rem' }}>Delivery Brk</TableCell>
                                    <TableCell align="right" sx={{ ...headerCellSx, fontSize: '0.65rem' }}>Intra Brk</TableCell>
                                    <TableCell align="right" sx={{ ...headerCellSx, fontSize: '0.65rem' }}>Future Brk</TableCell>
                                    <TableCell align="right" sx={{ ...headerCellSx, fontSize: '0.65rem' }}>Option Brk</TableCell>
                                    <TableCell align="right" sx={{ ...headerCellSx, fontSize: '0.65rem' }}>Total Brk</TableCell>
                                    <TableCell align="right" sx={{ ...headerCellSx, fontSize: '0.65rem' }}>Day P&L</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {row.dayBreakdowns.map((day) => (
                                    <TableRow key={day.date} hover sx={{ '&:hover': { bgcolor: '#F5F5F5' } }}>
                                      <TableCell sx={{ fontSize: '0.8rem', color: COLORS.textPrimary }}>
                                        {new Date(day.date).toLocaleDateString('en-IN', {
                                          weekday: 'short', day: 'numeric', month: 'short', year: '2-digit',
                                        })}
                                      </TableCell>
                                      <TableCell align="right" sx={{ ...currencyCellSx, fontSize: '0.8rem' }}>{day.trades}</TableCell>
                                      <TableCell align="right" sx={{ ...currencyCellSx, fontSize: '0.8rem', color: COLORS.warning, bgcolor: '#FFFBEB' }}>{formatCurrency(day.equityBrk)}</TableCell>
                                      <TableCell align="right" sx={{ ...currencyCellSx, fontSize: '0.8rem', color: COLORS.warning, bgcolor: '#FFFBEB' }}>{formatCurrency(day.intraBrk)}</TableCell>
                                      <TableCell align="right" sx={{ ...currencyCellSx, fontSize: '0.8rem', color: COLORS.warning, bgcolor: '#FFFBEB' }}>{formatCurrency(day.futureBrk)}</TableCell>
                                      <TableCell align="right" sx={{ ...currencyCellSx, fontSize: '0.8rem', color: COLORS.warning, bgcolor: '#FFFBEB' }}>{formatCurrency(day.optionBrk)}</TableCell>
                                      <TableCell align="right">
                                        <Box
                                          component="span"
                                          sx={{
                                            display: 'inline-block',
                                            px: 0.85,
                                            py: 0.25,
                                            borderRadius: 1,
                                            bgcolor: '#EEF2FF',
                                            color: '#4F46E5',
                                            fontSize: '0.78rem',
                                            fontWeight: 700,
                                            fontFeatureSettings: '"tnum"',
                                          }}
                                        >
                                          {formatCurrency(day.totalBrk)}
                                        </Box>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Box
                                          component="span"
                                          sx={{
                                            display: 'inline-block',
                                            px: 0.85,
                                            py: 0.25,
                                            borderRadius: 1,
                                            bgcolor: day.dayPnL >= 0 ? '#D1FAE5' : '#FEE2E2',
                                            color: day.dayPnL >= 0 ? '#065F46' : '#991B1B',
                                            fontSize: '0.78rem',
                                            fontWeight: 700,
                                            fontFeatureSettings: '"tnum"',
                                          }}
                                        >
                                          {formatCurrency(day.dayPnL)}
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Paper>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}

              {/* Totals Row */}
              {clientRows.length > 0 && (
                <TableRow sx={{ bgcolor: '#F8FAFC', '& td': { fontWeight: 700, borderTop: `1px solid ${COLORS.borderStrong}`, py: 1.75 } }}>
                  <TableCell />
                  <TableCell sx={{ color: COLORS.textPrimary, fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    Total · {clientRows.length} clients
                  </TableCell>
                  <TableCell align="right" sx={{ ...currencyCellSx, fontWeight: 700 }}>
                    {clientRows.reduce((s, r) => s + r.trades, 0)}
                  </TableCell>
                  <TableCell align="right" sx={{ ...currencyCellSx, color: COLORS.blue, fontWeight: 700 }}>
                    {formatCurrency(clientRows.reduce((s, r) => s + r.turnover, 0))}
                  </TableCell>
                  <TableCell align="right" sx={{ ...currencyCellSx, color: COLORS.warning, fontWeight: 700, bgcolor: '#FFFBEB' }}>
                    {formatCurrency(clientRows.reduce((s, r) => s + r.equityBrk, 0))}
                  </TableCell>
                  <TableCell align="right" sx={{ ...currencyCellSx, color: COLORS.warning, fontWeight: 700, bgcolor: '#FFFBEB' }}>
                    {formatCurrency(clientRows.reduce((s, r) => s + r.intraBrk, 0))}
                  </TableCell>
                  <TableCell align="right" sx={{ ...currencyCellSx, color: COLORS.warning, fontWeight: 700, bgcolor: '#FFFBEB' }}>
                    {formatCurrency(clientRows.reduce((s, r) => s + r.futureBrk, 0))}
                  </TableCell>
                  <TableCell align="right" sx={{ ...currencyCellSx, color: COLORS.warning, fontWeight: 700, bgcolor: '#FFFBEB' }}>
                    {formatCurrency(clientRows.reduce((s, r) => s + r.optionBrk, 0))}
                  </TableCell>
                  <TableCell align="right">
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        px: 1,
                        py: 0.4,
                        borderRadius: 1,
                        bgcolor: '#4F46E5',
                        color: '#FFFFFF',
                        fontSize: '0.88rem',
                        fontWeight: 800,
                        fontFeatureSettings: '"tnum"',
                      }}
                    >
                      {formatCurrency(summary.totalBrk)}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        px: 1,
                        py: 0.4,
                        borderRadius: 1,
                        bgcolor: summary.totalPnL >= 0 ? COLORS.positive : COLORS.negative,
                        color: '#FFFFFF',
                        fontSize: '0.88rem',
                        fontWeight: 800,
                        fontFeatureSettings: '"tnum"',
                      }}
                    >
                      {formatCurrency(summary.totalPnL)}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export default Analytics;
